import type { LayoutElement, PageDescriptor, ProjectDescriptor, SectionDescriptor, TableLayout } from "@kaleidoscope-solutions/engine-wasm"
import type { ModelFacade } from "@/utils/useEngineModel/ModelFacade"
import { defineStore } from "pinia"
import { slugify } from "@/utils"

interface DatasetLookup {
  name: string
}

type PageElementKind = "block" | "list" | "relationship"

interface PageElementReference {
  kind: PageElementKind
  name: string
}

export interface PageInputReference extends PageElementReference {
  sourcePagePath: string
}

export interface PageOutputReference extends PageElementReference {
  consumerPagePaths: string[]
}

interface ElementCollections {
  blockNames: Set<string>
  listNames: Set<string>
  relationshipNames: Set<string>
  viewNames: Set<string>
}

interface ElementCounts {
  blockCount: number
  listCount: number
  relationshipCount: number
  viewCount: number
}

interface SubPageAggregateCounts {
  subPageBlockCount: number
  subPageListCount: number
  subPageRelationshipCount: number
  subPageViewCount: number
  subPageInputCount: number
  subPageOutputCount: number
}

interface SubPageAggregateNames {
  subPageBlockNames: string[]
  subPageListNames: string[]
  subPageRelationshipNames: string[]
  subPageViewNames: string[]
  subPageInputNames: string[]
  subPageOutputNames: string[]
}

interface PageRouteLocation {
  name: "/projects/[project]/[page]/"
  params: {
    project: string
    page: string
  }
}

interface SubPageRouteLocation {
  name: "/projects/[project]/[page]/[subpage]"
  params: {
    project: string
    page: string
    subpage: string
  }
}

export type PageUsageRoute = PageRouteLocation | SubPageRouteLocation

export interface PageUsageRow extends ElementCounts, SubPageAggregateCounts, SubPageAggregateNames {
  id: string
  projectName: string
  pagePath: string
  route: PageUsageRoute
  elementReferences: PageElementReference[]
  blockNames: string[]
  listNames: string[]
  relationshipNames: string[]
  viewNames: string[]
  inputs: PageInputReference[]
  outputs: PageOutputReference[]
}

interface State {
  model?: ModelFacade
  pageRows: PageUsageRow[]
  isLoading: boolean
  loadError: string | null
}

function createElementCollections(): ElementCollections {
  return {
    blockNames: new Set<string>(),
    listNames: new Set<string>(),
    relationshipNames: new Set<string>(),
    viewNames: new Set<string>()
  }
}

function toElementCounts(collections: ElementCollections): ElementCounts {
  const blockCount = collections.blockNames.size
  const listCount = collections.listNames.size
  const relationshipCount = collections.relationshipNames.size
  const viewCount = collections.viewNames.size

  return {
    blockCount,
    listCount,
    relationshipCount,
    viewCount,
  }
}

function sortNames(values: Iterable<string>): string[] {
  return [...new Set(values)].sort((left, right) => left.localeCompare(right))
}

function toPageElementReferences(collections: ElementCollections): PageElementReference[] {
  const references: PageElementReference[] = []

  for (const blockName of collections.blockNames) {
    references.push({ kind: "block", name: blockName })
  }

  for (const listName of collections.listNames) {
    references.push({ kind: "list", name: listName })
  }

  for (const relationshipName of collections.relationshipNames) {
    references.push({ kind: "relationship", name: relationshipName })
  }

  return references.sort((left, right) => {
    if (left.kind !== right.kind) {
      return left.kind.localeCompare(right.kind)
    }

    return left.name.localeCompare(right.name)
  })
}

function normalizeElementKey(reference: PageElementReference): string {
  return `${reference.kind}:${reference.name.trim().toLowerCase()}`
}

function withComputedPageIO(rows: PageUsageRow[]): PageUsageRow[] {
  const ownerByElement = new Map<string, { rowId: string; pagePath: string; ref: PageElementReference }>()
  const usageByElement = new Map<string, Set<string>>()
  const rowById = new Map(rows.map((row) => [row.id, row]))

  for (const row of rows) {
    for (const ref of row.elementReferences) {
      const key = normalizeElementKey(ref)
      const usage = usageByElement.get(key) ?? new Set<string>()
      usage.add(row.id)
      usageByElement.set(key, usage)

      if (!ownerByElement.has(key)) {
        ownerByElement.set(key, {
          rowId: row.id,
          pagePath: row.pagePath,
          ref
        })
      }
    }
  }

  return rows.map((row) => {
    const inputs: PageInputReference[] = []
    const outputs: PageOutputReference[] = []
    const seenInputKeys = new Set<string>()
    const seenOutputKeys = new Set<string>()

    for (const ref of row.elementReferences) {
      const key = normalizeElementKey(ref)
      const owner = ownerByElement.get(key)
      const usage = usageByElement.get(key)
      if (!owner || !usage) {
        continue
      }

      if (owner.rowId !== row.id && !seenInputKeys.has(key)) {
        inputs.push({
          ...ref,
          sourcePagePath: owner.pagePath
        })
        seenInputKeys.add(key)
      }

      if (owner.rowId === row.id && usage.size > 1 && !seenOutputKeys.has(key)) {
        const consumerPagePaths = [...usage]
          .filter((rowId) => rowId !== row.id)
          .map((rowId) => rowById.get(rowId)?.pagePath)
          .filter((pagePath): pagePath is string => !!pagePath)
          .sort((left, right) => left.localeCompare(right))

        outputs.push({
          ...owner.ref,
          consumerPagePaths
        })
        seenOutputKeys.add(key)
      }
    }

    return {
      ...row,
      inputs,
      outputs
    }
  })
}

function routeForPage(
  projectName: string,
  pageName: string,
  topLevelPageName: string,
  depth: number
): PageUsageRoute {
  if (depth <= 0) {
    return {
      name: "/projects/[project]/[page]/",
      params: {
        project: slugify(projectName),
        page: slugify(pageName)
      }
    }
  }

  if (depth === 1) {
    return {
      name: "/projects/[project]/[page]/[subpage]",
      params: {
        project: slugify(projectName),
        page: slugify(topLevelPageName),
        subpage: slugify(pageName)
      }
    }
  }

  return {
    name: "/projects/[project]/[page]/",
    params: {
      project: slugify(projectName),
      page: slugify(topLevelPageName)
    }
  }
}

function tableLayoutElements(tableLayout: TableLayout) {
  return [
    ...(tableLayout.rowElements as LayoutElement[]),
    ...(tableLayout.columnElements as LayoutElement[]),
    ...(tableLayout.contextElements as LayoutElement[]),
    ...(tableLayout.rowGroups as LayoutElement[]),
    ...(tableLayout.columnGroups as LayoutElement[])
  ]
}

function rowAndColumnLayoutElements(tableLayout: TableLayout) {
  return [
    ...(tableLayout.rowElements as LayoutElement[]),
    ...(tableLayout.columnElements as LayoutElement[])
  ]
}

function isRelationshipNameFromLayoutElement(name: string) {
  const trimmedName = name.trim()
  return trimmedName.includes("::") && !trimmedName.toLowerCase().endsWith("::all")
}

function toNodePath(rowId: string): string {
  const separatorIndex = rowId.lastIndexOf("::")
  return separatorIndex >= 0 ? rowId.slice(separatorIndex + 2) : rowId
}

function withSubPageAggregates(rows: PageUsageRow[]): PageUsageRow[] {
  const rowsWithNodePath = rows.map((row) => ({
    row,
    nodePath: toNodePath(row.id)
  }))

  return rowsWithNodePath.map((candidate) => {
    const aggregateCounts: SubPageAggregateCounts = {
      subPageBlockCount: 0,
      subPageListCount: 0,
      subPageRelationshipCount: 0,
      subPageViewCount: 0,
      subPageInputCount: 0,
      subPageOutputCount: 0
    }
    const subPageBlockNames = new Set<string>()
    const subPageListNames = new Set<string>()
    const subPageRelationshipNames = new Set<string>()
    const subPageViewNames = new Set<string>()
    const subPageInputNames = new Set<string>()
    const subPageOutputNames = new Set<string>()

    for (const possibleSubPage of rowsWithNodePath) {
      if (possibleSubPage.row.projectName !== candidate.row.projectName) {
        continue
      }

      if (!possibleSubPage.nodePath.startsWith(`${candidate.nodePath}.`)) {
        continue
      }

      aggregateCounts.subPageBlockCount += possibleSubPage.row.blockCount
      aggregateCounts.subPageListCount += possibleSubPage.row.listCount
      aggregateCounts.subPageRelationshipCount += possibleSubPage.row.relationshipCount
      aggregateCounts.subPageViewCount += possibleSubPage.row.viewCount
      aggregateCounts.subPageInputCount += possibleSubPage.row.inputs.length
      aggregateCounts.subPageOutputCount += possibleSubPage.row.outputs.length

      for (const name of possibleSubPage.row.blockNames) {
        subPageBlockNames.add(name)
      }
      for (const name of possibleSubPage.row.listNames) {
        subPageListNames.add(name)
      }
      for (const name of possibleSubPage.row.relationshipNames) {
        subPageRelationshipNames.add(name)
      }
      for (const name of possibleSubPage.row.viewNames) {
        subPageViewNames.add(name)
      }
      for (const input of possibleSubPage.row.inputs) {
        subPageInputNames.add(input.name)
      }
      for (const output of possibleSubPage.row.outputs) {
        subPageOutputNames.add(output.name)
      }
    }

    return {
      ...candidate.row,
      ...aggregateCounts,
      subPageBlockNames: sortNames(subPageBlockNames),
      subPageListNames: sortNames(subPageListNames),
      subPageRelationshipNames: sortNames(subPageRelationshipNames),
      subPageViewNames: sortNames(subPageViewNames),
      subPageInputNames: sortNames(subPageInputNames),
      subPageOutputNames: sortNames(subPageOutputNames),
    }
  })
}

export const usePagesStore = defineStore("pages", {
  state: (): State => ({
    model: undefined,
    pageRows: [],
    isLoading: true,
    loadError: null
  }),
  actions: {
    initialise(model: ModelFacade) {
      this.model = model
    },
    async getDataset(datasetName: string, cache: Map<string, DatasetLookup>) {
      const cachedDataset = cache.get(datasetName)
      if (cachedDataset) {
        return cachedDataset
      }

      const dataset = await this.modelApi.dataset(datasetName)
      const mappedDataset: DatasetLookup = {
        name: dataset.name
      }

      cache.set(datasetName, mappedDataset)
      return mappedDataset
    },
    async getDatasetDeduped(
      datasetName: string,
      cache: Map<string, DatasetLookup>,
      pendingCache: Map<string, Promise<DatasetLookup>>
    ) {
      const cachedDataset = cache.get(datasetName)
      if (cachedDataset) {
        return cachedDataset
      }

      const pendingDataset = pendingCache.get(datasetName)
      if (pendingDataset) {
        return await pendingDataset
      }

      const pending = this.getDataset(datasetName, cache)
      pendingCache.set(datasetName, pending)

      try {
        return await pending
      } finally {
        pendingCache.delete(datasetName)
      }
    },
    async getTableLayout(viewName: string, cache: Map<string, TableLayout>) {
      const cachedLayout = cache.get(viewName)
      if (cachedLayout) {
        return cachedLayout
      }

      const tableLayout = await this.modelApi.tableLayout(viewName)
      cache.set(viewName, tableLayout)
      return tableLayout
    },
    async getTableLayoutDeduped(
      viewName: string,
      cache: Map<string, TableLayout>,
      pendingCache: Map<string, Promise<TableLayout>>
    ) {
      const cachedLayout = cache.get(viewName)
      if (cachedLayout) {
        return cachedLayout
      }

      const pendingLayout = pendingCache.get(viewName)
      if (pendingLayout) {
        return await pendingLayout
      }

      const pending = this.getTableLayout(viewName, cache)
      pendingCache.set(viewName, pending)

      try {
        return await pending
      } finally {
        pendingCache.delete(viewName)
      }
    },
    async collectSectionElements(
      section: SectionDescriptor,
      collections: ElementCollections,
      datasetCache: Map<string, DatasetLookup>,
      tableLayoutCache: Map<string, TableLayout>,
      datasetPendingCache: Map<string, Promise<DatasetLookup>>,
      tableLayoutPendingCache: Map<string, Promise<TableLayout>>
    ) {
      for (const subSection of section.sections) {
        await this.collectSectionElements(subSection, collections, datasetCache, tableLayoutCache, datasetPendingCache, tableLayoutPendingCache)
      }

      for (const widget of section.widgets) {
        if (widget.kind !== "table") {
          continue
        }

        collections.viewNames.add(widget.view)

        const tableLayout = await this.getTableLayoutDeduped(widget.view, tableLayoutCache, tableLayoutPendingCache)

        // Relationship usage can also be inferred directly from row/column names
        // when they include key-type pairs (for example "A::B").
        for (const layoutElement of rowAndColumnLayoutElements(tableLayout)) {
          if (isRelationshipNameFromLayoutElement(layoutElement.name)) {
            collections.relationshipNames.add(layoutElement.name)
          }
        }

        for (const layoutElement of tableLayoutElements(tableLayout)) {
          if (layoutElement.isDataset) {
            const dataset = await this.getDatasetDeduped(layoutElement.name, datasetCache, datasetPendingCache)

            collections.blockNames.add(dataset.name)
          } else {
            collections.listNames.add(layoutElement.name)
          }
        }
      }
    },
    async buildPageRows(
      pages: readonly PageDescriptor[],
      projectName: string,
      datasetCache: Map<string, DatasetLookup>,
      tableLayoutCache: Map<string, TableLayout>,
      datasetPendingCache: Map<string, Promise<DatasetLookup>>,
      tableLayoutPendingCache: Map<string, Promise<TableLayout>>,
      depth = 0,
      parentPath = "",
      parentNodeId = "",
      topLevelPageName = ""
    ) {
      const rows: PageUsageRow[] = []

      for (const [index, page] of pages.entries()) {
        const pagePath = parentPath ? `${parentPath} / ${page.name}` : page.name
        const nodeId = parentNodeId ? `${parentNodeId}.${index}` : `${index}`
        const resolvedTopLevelPageName = depth === 0 ? page.name : topLevelPageName

        const collections = createElementCollections()

        for (const section of page.sections) {
          await this.collectSectionElements(section, collections, datasetCache, tableLayoutCache, datasetPendingCache, tableLayoutPendingCache)
        }

        rows.push({
          id: `${projectName}::${nodeId}`,
          projectName,
          pagePath,
          route: routeForPage(projectName, page.name, resolvedTopLevelPageName, depth),
          elementReferences: toPageElementReferences(collections),
          blockNames: sortNames(collections.blockNames),
          listNames: sortNames(collections.listNames),
          relationshipNames: sortNames(collections.relationshipNames),
          viewNames: sortNames(collections.viewNames),
          inputs: [],
          outputs: [],
          subPageBlockCount: 0,
          subPageListCount: 0,
          subPageRelationshipCount: 0,
          subPageViewCount: 0,
          subPageInputCount: 0,
          subPageOutputCount: 0,
          subPageBlockNames: [],
          subPageListNames: [],
          subPageRelationshipNames: [],
          subPageViewNames: [],
          subPageInputNames: [],
          subPageOutputNames: [],
          ...toElementCounts(collections)
        })

        if (page.subPages?.length) {
          const subPageRows = await this.buildPageRows(
            page.subPages,
            projectName,
            datasetCache,
            tableLayoutCache,
            datasetPendingCache,
            tableLayoutPendingCache,
            depth + 1,
            pagePath,
            nodeId,
            resolvedTopLevelPageName
          )

          rows.push(...subPageRows)
        }
      }

      return rows
    },
    async loadPages(projects: readonly ProjectDescriptor[]) {
      try {
        this.isLoading = true
        this.loadError = null

        const rows: PageUsageRow[] = []

        for (const project of projects) {
          const datasetCache = new Map<string, DatasetLookup>()
          const tableLayoutCache = new Map<string, TableLayout>()
          const datasetPendingCache = new Map<string, Promise<DatasetLookup>>()
          const tableLayoutPendingCache = new Map<string, Promise<TableLayout>>()
          const projectRows = withComputedPageIO(
            await this.buildPageRows(project.pages, project.name, datasetCache, tableLayoutCache, datasetPendingCache, tableLayoutPendingCache)
          )

          rows.push(...projectRows)
        }

        const rowsWithSubPageAggregates = withSubPageAggregates(rows)
        this.pageRows = rowsWithSubPageAggregates.sort((left, right) => {
          return left.projectName.localeCompare(right.projectName) || left.pagePath.localeCompare(right.pagePath)
        })
      } catch (error) {
        this.loadError = error instanceof Error ? error.message : "Unable to load pages"
      } finally {
        this.isLoading = false
      }
    }
  },
  getters: {
    modelApi(state) {
      if (state.model === undefined) {
        throw new Error("Model not initialised")
      }
      return state.model?.api
    },
  }
})
